<template>
  <sistema>
    <container :isModal="false" :loading="!stateReady">
      <template v-slot:header>
        <h3>Faturamento RNTRC</h3>
      </template>
      <template v-slot:body>
        <form @submit.prevent="submitSearch">
          <entity-selector-cliente
            v-if="amIAllowed('view', 'root-cliente')"
            :idClienteFiltered="filter.idCliente ?? undefined"
            :canEdit="true"
            @confirm-selection="confirmSelectionCliente"
            :parentLoading="stateLoading"
          />
          <fieldset :disabled="stateLoading">
            <div class="p-formgrid p-grid">
              <div class="p-field p-fluid p-col-12 p-md-6 p-lg-3">
                <input-with-label
                  label="Competência"
                  v-slot="l"
                  :required="true"
                >
                  <Calendar
                    :id="l.id"
                    v-model="competencia"
                    view="month"
                    dateFormat="mm/yy"
                    :showIcon="true"
                    :required="true"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-6 p-lg-3">
                <input-with-label label="Tipo" v-slot="l">
                  <Dropdown
                    :id="l.id"
                    v-model="filter.flTipoServico"
                    :options="tiposDisponiveis"
                    optionValue="flag"
                    optionLabel="descricao"
                    :showClear="true"
                    :placeholder="'&nbsp;'"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-6 p-lg-3">
                <input-with-label label="Status do item" v-slot="l">
                  <Dropdown
                    :id="l.id"
                    v-model="filter.flStatusItem"
                    :options="statusDisponiveis"
                    optionValue="flag"
                    optionLabel="descricao"
                    :showClear="true"
                    :placeholder="'&nbsp;'"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-6 p-lg-3">
                <input-with-label label="Placa" v-slot="l">
                  <InputText
                    :id="l.id"
                    v-model="filter.placa"
                    maxlength="8"
                    @blur="filter.placa = normalizarPlaca(filter.placa)"
                  />
                </input-with-label>
              </div>
              <div class="p-col-12">
                <button-group>
                  <Button
                    type="submit"
                    class="p-button-rounded"
                    label="Buscar"
                    :disabled="!competencia"
                  />
                  <Button
                    label="Baixar xlsx"
                    icon="pi pi-file-excel"
                    class="p-button-rounded p-button-success"
                    :disabled="!competencia"
                    @click="downloadXlsx()"
                  />
                </button-group>
              </div>
            </div>
          </fieldset>
        </form>

        <div class="rntrc-totais p-mb-3" v-if="buscou">
          <div class="rntrc-total-card">
            <span class="rntrc-total-label">Total do período</span>
            <span class="rntrc-total-valor" v-text="formatMoeda(totalGeral)" />
          </div>
          <div
            class="rntrc-total-card"
            v-for="t in totaisPorStatus"
            :key="t.flag"
          >
            <span class="rntrc-total-label" v-text="t.descricao" />
            <span class="rntrc-total-valor">
              {{ t.quantidade }}
              <small> {{ t.quantidade !== 1 ? "itens" : "item" }}</small>
            </span>
            <small v-text="formatMoeda(t.valor)" />
          </div>
        </div>

        <search-result
          :results="results"
          :keyParam="'idRntrcFaturamentoItem'"
          :loading="stateLoading"
          :hideActions="true"
          @after-page-change="afterPageChange"
        >
          <template v-slot:title>
            <span class="search-result-title">Serviços realizados</span>
          </template>
          <template v-slot:columns>
            <Column header="Cliente" v-if="amIAllowed('view', 'root-cliente')">
              <template #body="{ data }">
                {{ data.nomeCliente }}
              </template>
            </Column>
            <Column header="Solicitação" style="width: 7rem">
              <template #body="{ data }">
                <router-link
                  v-if="data.idRntrcSolicitacao"
                  :to="{
                    name: 'rntrc-solicitacao-detalhe',
                    params: { idRntrcSolicitacao: data.idRntrcSolicitacao + '' }
                  }"
                  v-text="data.idRntrcSolicitacao"
                />
              </template>
            </Column>
            <Column header="Placa" style="width: 7rem">
              <template #body="{ data }">
                <b v-text="formatPlaca(data.placa) || '---'" />
              </template>
            </Column>
            <Column header="Tipo">
              <template #body="{ data }">
                {{ getTipoServicoLabel(data.flTipoServico) }}
                <small v-if="data.flServicoVeiculo">
                  ({{ getServicoVeiculoLabel(data.flServicoVeiculo) }})</small
                >
              </template>
            </Column>
            <Column header="Transportador">
              <template #body="{ data }">
                <div v-text="data.nomeTransportador" />
                <small v-text="formatCpfCnpj(data.cpfCnpj)" />
              </template>
            </Column>
            <Column header="Solicitado em" style="width: 9rem">
              <template #body="{ data }">
                {{ formatDateTime(data.dataSolicitacao) }}
              </template>
            </Column>
            <Column header="PIX pago em" style="width: 9rem">
              <template #body="{ data }">
                {{ formatDateTime(data.dataPagamentoPix) || "---" }}
              </template>
            </Column>
            <Column header="Valor (taxa ANTT)" style="width: 9rem">
              <template #body="{ data }">
                {{ formatMoeda(data.valor) }}
              </template>
            </Column>
            <Column header="Status" style="width: 8rem">
              <template #body="{ data }">
                <span
                  class="aet-badge"
                  :class="'aet-badge--' + getStatusItemCor(data.flStatusItem)"
                  v-text="getStatusItemLabel(data.flStatusItem)"
                />
              </template>
            </Column>
          </template>
        </search-result>
      </template>
    </container>
  </sistema>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  Ref
} from "vue";
import EntitySelectorCliente from "../../components/cliente/EntitySelectorCliente.vue";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { formatDateTime } from "../../modules/utils";
import { PaginationResult, defaultResults } from "../../pagination";
import { ClientePayload } from "../../modules/business/cliente/clienteBusiness";
import { useRntrcFaturamentoService } from "../../modules/business/rntrc/rntrcFaturamentoBusiness";
import {
  RntrcFaturamentoFilterPayload,
  RntrcFaturamentoItemPayload,
  RntrcFaturamentoResumoPayload,
  STATUS_ITEM_FATURAMENTO_RNTRC,
  TIPOS_SERVICO_RNTRC,
  formatCpfCnpj,
  formatMoeda,
  formatPlaca,
  getServicoVeiculoLabel,
  getStatusItemCor,
  getStatusItemLabel,
  getTipoServicoLabel,
  normalizarPlaca,
  toCompetencia
} from "../../modules/business/rntrc/rntrcTypes";

interface RntrcFaturamentoState {
  stateReady: boolean;
  loadings: Array<Ref<boolean>>;
  competencia: Date | null;
  filter: RntrcFaturamentoFilterPayload;
  page: number;
  results: PaginationResult<RntrcFaturamentoItemPayload>;
  resumo: Array<RntrcFaturamentoResumoPayload>;
  buscou: boolean;
}

export default defineComponent({
  setup() {
    const hoje = new Date();
    const state = reactive<RntrcFaturamentoState>({
      stateReady: false,
      loadings: [],
      competencia: new Date(hoje.getFullYear(), hoje.getMonth(), 1),
      filter: {},
      page: 0,
      results: defaultResults(),
      resumo: [],
      buscou: false
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIAllowed(authority, permissionInfo);
    };

    const stateLoading = computed(() => state.loadings.length !== 0);

    const removeLoading = (loading: Ref<boolean>) => {
      const idx = state.loadings.indexOf(loading);
      if (idx >= 0) {
        state.loadings.splice(idx, 1);
      }
    };

    const filtroAtual = (): RntrcFaturamentoFilterPayload => ({
      ...state.filter,
      competencia: toCompetencia(state.competencia),
      placa: normalizarPlaca(state.filter.placa) || null
    });

    const executeSearch = () => {
      const { data, loading, listarItens } = useRntrcFaturamentoService();
      state.loadings.push(loading);
      return listarItens(filtroAtual(), state.page)
        .then(() => {
          state.results =
            data.value && data.value.content ? data.value : defaultResults();
          state.page = state.results.page.number;
          removeLoading(loading);
        })
        .catch(() => removeLoading(loading));
    };

    const executeResumo = () => {
      const { data, loading, resumo } = useRntrcFaturamentoService();
      state.loadings.push(loading);
      return resumo(filtroAtual())
        .then(() => {
          state.resumo = Array.isArray(data.value) ? data.value : [];
          removeLoading(loading);
        })
        .catch(() => removeLoading(loading));
    };

    const submitSearch = () => {
      if (!state.competencia) {
        return;
      }
      state.page = 0;
      state.buscou = true;
      executeSearch();
      executeResumo();
    };

    const afterPageChange = (page?: number) => {
      if (typeof page !== "undefined") {
        state.page = page;
      }
      executeSearch();
    };

    const downloadXlsx = () => {
      if (!state.competencia) {
        return;
      }
      const { loading, downloadXlsx } = useRntrcFaturamentoService();
      state.loadings.push(loading);
      downloadXlsx(filtroAtual())
        .then(() => removeLoading(loading))
        .catch(() => removeLoading(loading));
    };

    const confirmSelectionCliente = (c?: ClientePayload) => {
      state.filter.idCliente = c && c.idCliente ? c.idCliente : null;
    };

    const totalGeral = computed(() =>
      state.resumo.map((r) => r.valorTotal ?? 0).reduce((a, b) => a + b, 0)
    );

    const totaisPorStatus = computed(() =>
      STATUS_ITEM_FATURAMENTO_RNTRC.map((s) => {
        const linhas = state.resumo.filter((r) => r.flStatusItem === s.flag);
        return {
          flag: s.flag,
          descricao: s.descricao,
          quantidade: linhas
            .map((r) => r.quantidade ?? 0)
            .reduce((a, b) => a + b, 0),
          valor: linhas.map((r) => r.valorTotal ?? 0).reduce((a, b) => a + b, 0)
        };
      }).filter((t) => t.quantidade > 0)
    );

    onMounted(() => {
      state.stateReady = true;
      submitSearch();
    });

    return {
      amIAllowed,
      stateLoading,
      tiposDisponiveis: TIPOS_SERVICO_RNTRC,
      statusDisponiveis: STATUS_ITEM_FATURAMENTO_RNTRC,
      submitSearch,
      afterPageChange,
      downloadXlsx,
      confirmSelectionCliente,
      totalGeral,
      totaisPorStatus,
      normalizarPlaca,
      formatDateTime,
      formatCpfCnpj,
      formatMoeda,
      formatPlaca,
      getServicoVeiculoLabel,
      getStatusItemCor,
      getStatusItemLabel,
      getTipoServicoLabel,
      ...toRefs(state)
    };
  },
  components: {
    EntitySelectorCliente
  }
});
</script>

<style scoped lang="scss">
.rntrc-totais {
  display: flex;
  flex-wrap: wrap;
  gap: $space-3;
}

.rntrc-total-card {
  display: flex;
  flex-direction: column;
  padding: $space-3 $space-4;
  background: $bg-surface;
  border: 1px solid $bg-surface-secondary;
  border-left: 4px solid $primary;
  border-radius: $radius-lg;
  min-width: 180px;
}

.rntrc-total-label {
  font-size: $font-size-xs;
  font-weight: $font-weight-semibold;
  color: $text-muted;
  text-transform: uppercase;
}

.rntrc-total-valor {
  font-size: $font-size-xl;
  font-weight: $font-weight-bold;
}
</style>
